import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidateDeviation } from 'src/api/deviation'
import { requestProvider } from 'src/api/requestProvider'
import { useUserCtx } from 'src/hooks/context-hooks'
import { AppRoutesNavigation } from 'src/app-routes'
import { TaskTypes } from 'src/enums'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'

type Props = {
  customerId: EntityId
  localityId: EntityId
  deviation: { id: number; title: string; description?: string | null; status: boolean }
}

export const CreateWorkOrderFromDeviationButton: React.FC<Props> = ({ customerId, localityId, deviation }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const navigate = useNavigate()
  const { user } = useUserCtx()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(`AO: ${deviation.title}`)
  const [description, setDescription] = useState(deviation.description ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpen = () => {
    setTitle(`AO: ${deviation.title}`)
    setDescription(deviation.description ?? '')
    setOpen(true)
  }

  const handleClose = () => {
    if (!isSubmitting) setOpen(false)
  }

  const handleCreate = async () => {
    setIsSubmitting(true)
    try {
      const createResponse = await requestProvider.post<number[]>(`customers/${customerId}/tasks`, {
        title,
        description,
        type: TaskTypes.Once,
        startDate: new Date().toISOString(),
        duration: 1,
        localityIds: [Number(localityId)],
        responsiblePerson: user
          ? { id: user.id, email: user.email, firstName: user.firstName, lastName: user.lastName }
          : undefined,
        responsibleUserId: user?.id,
      })

      const scheduledTaskIds: number[] = Array.isArray(createResponse.data) ? createResponse.data : []

      if (scheduledTaskIds.length === 0) {
        toast.error(t('addingError', { ns: 'common' }))
        return
      }

      const scheduledTaskId = scheduledTaskIds[0]

      await requestProvider.post(
        `localities/${localityId}/scheduledTask/${scheduledTaskId}/add-deviations-to-work-order`,
        [deviation.id],
      )

      invalidateDeviation.getDeviation(customerId, deviation.id)
      invalidateLocalityCache.getLocalityWithActivityCounts(customerId, localityId as EntityId)
      invalidateScheduledTaskCache.useScheduledTasks(localityId)
      invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId)

      setOpen(false)
      navigate(AppRoutesNavigation.Localities.workOrder(customerId, localityId, scheduledTaskId))
    } catch {
      toast.error(t('addingError', { ns: 'common' }))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <GridActionButton
        buttonText={t('createWorkOrderFromDeviation', { ns: 'deviation' })}
        onClick={handleOpen}
        variant="outlined"
        shadowWeight={null}
        width="auto"
        disabled={deviation.status}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('createWorkOrderFromDeviation', { ns: 'deviation' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('title', { ns: 'common' })}
              value={title}
              onChange={e => setTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('description', { ns: 'common' })}
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={isSubmitting || !title.trim()}>
            {t('create', { ns: 'common' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
